import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { format } from 'date-fns';
import {
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Theme,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import GetAppIcon from '@mui/icons-material/GetApp';
import BlockIcon from '@mui/icons-material/Block';
import DoneIcon from '@mui/icons-material/Done';
import UploadIcon from '@mui/icons-material/CloudUpload';
import PrintIcon from '@mui/icons-material/Print';
import { DownloadDocumentIcon } from '@widgets';
import { PDFView } from '@widgets/PDFView';
import {
  useAccountCustomerStore,
  useAccountDetailsStore,
  useAccountsByCustomerStore,
  useApplicationsByCustomerStore,
  useApproveRequestStore,
  useCardsByAccountStore,
  useChangeCreditLimitStore,
  useCreditLimitRequestsStore,
  useCreditLimitStore,
  useCreditPdfStore,
  useDownloadStatementStore,
  useRejectRequestStore,
  useSetPartnerAccountNumberStore,
  useStatementsStore,
  useUploadCreditFormStore,
  useUploadedCreditPdfStore,
} from '@lib';
import { RequestEntity, StatementEntity, StatementSearchParams, TransactionEntity, ApiUtils } from '@shared/api';
import { AmountUtils, DateUtils, Input, PermissionsUtils, RoutePaths, useDebounce, usePrevious, useRouteHook } from '@shared';
import { PermissionLevel, PermissionType, RoleType } from '@shared/types';
import { CardsList } from './widgets';
import { Button, DelayedLinearProgress, IconButton, SnackbarContext } from '@widgets';
import { CustomDatePicker } from '@pages/Applications/widgets/CustomDatePicker';
import { BankAccountInput } from '@pages/index';
import { Breadcrumbs } from '@mui/material';
import { Link } from '@mui/material';

const updateAccountCurrency = (currency: string) => {
  switch (currency) {
    case 'EUR':
      return '€';
    case 'CHF':
      return 'CHF';
    case 'USD':
      return '$';
    case 'GBP':
      return '£';
    default:
      return '';
  }
};

const useStyles = makeStyles<Theme>((theme) => ({
  uppercase: {
    textTransform: 'uppercase',
  },
  postTransactionContainer: {
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.palette.primary.main,
  },
  card: {
    marginBottom: theme.spacing(2),
  },
  cardItems: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'stretch',
    alignContent: 'center',
  },
  creditLimitRow: {
    [theme.breakpoints.up('sm')]: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',

      '& > *': {
        flexBasis: '25%',
      },
    },
    [theme.breakpoints.down('md')]: {
      marginBottom: '12px',
    },
  },
  accDetailsBtn: {
    marginRight: '12px',
  },
}));

const defaultDialogResponse = 'Are you sure you want to proceed?';

type Params = {
  selectedCustomerId: string;
  selectedAccountId: string;
};

export const AccountDetailsPage = () => {
  const { selectedAccountId, selectedCustomerId } = useParams<Params>();
  const { navigate } = useRouteHook();

  const { loading: loadingCustomers, customer, getCustomer, clear: clearCustomer } = useAccountCustomerStore();
  const { loading: loadingCards, cards, getCardsByAccount } = useCardsByAccountStore();
  const { applications, getApplicationsByCustomer } = useApplicationsByCustomerStore();
  const { loading: loadingAccounts, account, getAccountsByCustomer } = useAccountsByCustomerStore();
  const { account: accountDetails, getAccountDetails } = useAccountDetailsStore();
  const { loading: loadingCreditLimit, creditLimit, getCreditLimit } = useCreditLimitStore();
  const {
    loading: loadingChangeCreditLimit,
    error: errorChangeCreditLimit,
    creditLimit: changedCreditLimit,
    changeCreditLimit,
  } = useChangeCreditLimitStore();
  const { loading: loadingRequests, creditLimitRequests: requests, getCreditLimitRequests } = useCreditLimitRequestsStore();
  const {
    loading: loadingApproveRequest,
    error: errorApproveRequest,
    success: successApprovedRequest,
    approveRequest,
    clear: clearApprovedRequest,
  } = useApproveRequestStore();
  const {
    loading: loadingRejectRequest,
    error: errorRejectRequest,
    success: successRejectedRequest,
    rejectRequest,
    clear: clearRejectedRequest,
  } = useRejectRequestStore();
  const {
    loading: loadingSetPartnerAccountNumber,
    error: errorSetPartnerAccountNumber,
    success: successSetPartnerAccountNumber,
    setPartnerAccountNumber,
    clear: clearSetPartnerAccountNumber,
  } = useSetPartnerAccountNumberStore();
  const { getUploadedCreditPdf } = useUploadedCreditPdfStore();
  const {
    loading: loadingUploadCreditForm,
    error: errorUploadCreditForm,
    success: successUploadedCreditForm,
    uploadCreditForm,
    clear: clearUploadedCreditForm,
  } = useUploadCreditFormStore();
  const { loading: loadingCreditPdf, creditPdf, getCreditPdf, clear: clearCreditPdf } = useCreditPdfStore();
  const { loading: loadingDownloadStatement, statementFile, downloadStatement, clear: clearDownloadedStatement } = useDownloadStatementStore();
  const { loading: loadingStatements, error: errorStatements, statements, getStatements } = useStatementsStore();

  const [showConfirm, setShowConfirm] = useState(true);
  const [downloadingPdfRequestId, setDownloadingPdfRequestId] = useState<string>('');
  const [uploadingPdfRequestId, setUploadingPdfRequestId] = useState<string>('');
  const [currentUploadPdfRequestId, setCurrentUploadPdfRequestId] = useState<string>('');
  const [openedCreditLimit, setOpenedCreditLimit] = useState<number>(0);
  const [preapprovedCreditLimit, setPreapprovedCreditLimit] = useState<number>(0);
  const [highestCreditRequested, setHighestCreditRequested] = useState<number>();
  const [creditLimitDialogOpen, setCreditLimitDialogOpen] = useState(false);
  const [requestsDialogOpen, setRequestsDialogOpen] = useState(false);
  const [requestsDialogResponse, setRequestsDialogResponse] = useState<string | undefined>();
  const [transactionsDialogOpen, setTransactionsDialogOpen] = useState(false);
  const [transactionsDialogResponse] = useState<string | undefined>();
  const [transactions] = useState<TransactionEntity[] | undefined>();
  const [loadingTransactions] = useState(true);
  const [statementsDialogOpen, setStatementsDialogOpen] = useState(false);
  const [statementsDialogResponse, setStatementsDialogResponse] = useState<string | undefined>('Select the range');
  const [dialogResponse, setDialogResponse] = useState<string>(defaultDialogResponse);
  const [approvingRequestId, setApprovingRequestId] = useState<string>('');
  const [rejectingRequestId, setRejectingRequestId] = useState<string>('');
  const [showUpdatePartnerAccountNumber, setShowUpdatePartnerAccountNumber] = useState(false);
  const [updatePartnerAccountNumberResult, setUpdatePartnerAccountNumberResult] = useState<string | undefined>();
  const [updatePartnerAccountNumberValue, setUpdatePartnerAccountNumberValue] = useState('');
  const [bankAccountNumberError, setBankAccountNumberError] = useState<string | undefined>();
  const [filterCriteria, setFilterCriteria] = useState<StatementSearchParams>({ AccountId: selectedAccountId });

  const classes = useStyles();
  const snackbar = useContext(SnackbarContext);

  const accountCurrency = useMemo(() => {
    if (!account) return;
    return updateAccountCurrency(account.productName.slice(-3).toUpperCase());
  }, [account]);

  useEffect(() => {
    if (!selectedCustomerId) return;
    getCustomer(selectedCustomerId);
    if (PermissionsUtils.hasPermission(PermissionType.APPLICATIONS, PermissionLevel.READ)) {
      getApplicationsByCustomer(selectedCustomerId);
    }

    return () => {
      clearCustomer();
    };
  }, []);

  const mainApplicationId = useMemo(() => {
    const mainApplication = applications.filter((item) => item.applicationType === 'Main')[0];
    return mainApplication ? mainApplication?.id : undefined;
  }, [applications]);

  const prevCustomerData = usePrevious(customer);
  useEffect(() => {
    if (!customer) return;
    if (prevCustomerData === customer) return;
    getAccountsByCustomer({ customerId: customer.id, accountId: selectedAccountId });
  }, [customer, prevCustomerData]);

  useEffect(() => {
    if (!account?.accountCode) return;
    getAccountDetails(account.accountCode);
  }, [account]);

  useEffect(() => {
    if (!PermissionsUtils.hasPermission(PermissionType.ACCOUNTS, PermissionLevel.READ)) return;
    if (!selectedAccountId) return;
    getCardsByAccount(selectedAccountId);
  }, [selectedAccountId]);

  useEffect(() => {
    if (!PermissionsUtils.hasPermission(PermissionType.CREDIT_LIMIT_REQUESTS, PermissionLevel.READ)) return;
    if (!selectedAccountId) return;
    getCreditLimit(selectedAccountId);
  }, [selectedAccountId]);

  const statementRows = useMemo(() => {
    return statements
      ? statements.map((item) => ({
          ...item,
          statementDate: DateUtils.formatDate(item.statementDate),
          dueDate: DateUtils.formatDate(item.dueDate),
        }))
      : undefined;
  }, [statements]);

  const checkBankAccountNumberIsValid = () => {
    const value = updatePartnerAccountNumberValue;
    const matchResult = value?.match(/[a-zA-Z0-9]{6}\.\d\d\.\d\d\.[a-zA-Z]{3}/gim);
    return !!value && !!matchResult;
  };
  const getCurrencyError = () => {
    const value = updatePartnerAccountNumberValue.slice(-3).toUpperCase();
    if (value !== account?.productName?.slice(-3).toUpperCase()) {
      return 'Partner Account Number does no match account currency';
    }
    return '';
  };
  const validateBankAccountNumber = () => {
    if (checkBankAccountNumberIsValid()) {
      const error = getCurrencyError();
      setBankAccountNumberError(getCurrencyError());
      if (error) return false;
      return true;
    } else {
      setBankAccountNumberError('Invalid Partner Account Number');
      return false;
    }
  };

  useEffect(() => {
    if (loadingChangeCreditLimit) return;
    if (errorChangeCreditLimit) {
      setDialogResponse(errorChangeCreditLimit);
    }
    if (!changedCreditLimit) return;
    setDialogResponse(changedCreditLimit.requestCreated ? 'Request created' : 'New credit limit was applied');
  }, [loadingChangeCreditLimit, errorChangeCreditLimit, changedCreditLimit]);

  const changeCreditLimitHandler = async (cardId: string, creditLimit: number, globalCreditLimit: number) => {
    setDialogResponse('Loading');
    changeCreditLimit({ cardId, creditLimit, globalCreditLimit });
  };

  useEffect(() => {
    if (loadingRequests || !requests) return;
    if (!requests.length) setRequestsDialogResponse('No requests to show');
  }, [loadingRequests, requests]);

  const getRequests = () => {
    if (!account) return;
    getCreditLimitRequests(account.accId);
  };

  const downloadCreditForm = async (request: RequestEntity) => {
    try {
      setDownloadingPdfRequestId(request.id);
      await downloadPdf(
        customer ? customer.fullname : '',
        (request.currentLimit || 0).toString(),
        request.proposedLimit,
        request.name,
        request.isCreditLimitRequest,
      );
      setDownloadingPdfRequestId('');
    } catch (error) {
      snackbar.error(ApiUtils.getError(error));
      setDownloadingPdfRequestId('');
    }
  };

  const downloadUploadedCreditForm = async (request: RequestEntity) => {
    try {
      setDownloadingPdfRequestId(request.id + 'uploaded');
      await downloadUploadedPdf(request.id, request.name);
      setDownloadingPdfRequestId('');
    } catch (err) {
      snackbar.error(ApiUtils.getError(err));
      setDownloadingPdfRequestId('');
    }
  };

  const uploadCreditFormHandler = (request: RequestEntity) => {
    setCurrentUploadPdfRequestId(request.id);
    document.getElementById('fileInput')?.click();
  };

  useEffect(() => {
    if (loadingApproveRequest) return;
    if (errorApproveRequest) {
      setRejectingRequestId('');
      return;
    }
    if (loadingApproveRequest || !successApprovedRequest) return;
    getRequests();
    snackbar.success('Request approved successfully');
    setApprovingRequestId('');
    clearApprovedRequest();
  }, [loadingApproveRequest, errorApproveRequest, successApprovedRequest]);

  const approveRequestHandler = async (request: RequestEntity) => {
    setApprovingRequestId(request.id);
    approveRequest(request.id);
  };

  useEffect(() => {
    if (loadingRejectRequest) return;
    if (errorRejectRequest) {
      setRejectingRequestId('');
      return;
    }
    if (!successRejectedRequest) return;
    getRequests();
    snackbar.success('Request rejected successfully');
    clearRejectedRequest();
  }, [loadingRejectRequest, errorRejectRequest, successRejectedRequest]);

  const rejectRequestHandler = async (request: RequestEntity) => {
    setRejectingRequestId(request.id);
    rejectRequest(request.id);
  };

  const onFilterChange = (name: string, e: Date | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | null) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let newVal: any;
    if (e instanceof Date) {
      newVal = e;
    } else if (e) {
      newVal = e.currentTarget.value;
    } else {
      newVal = null;
    }

    //.getMonth(), 1) : null;// format(newVal, 'MMMM yyyy');
    const fullDate = newVal ? newVal.getFullYear() + '-' + format(newVal, 'MM') + '-01' : null;

    setFilterCriteria((curFilterCriteria) => ({
      ...curFilterCriteria,
      [name]: fullDate,
    }));
  };

  const debouncedFilterCriteria = useDebounce(filterCriteria, 2);

  useEffect(() => {
    if (loadingStatements) return;
    if (errorStatements) {
      setStatementsDialogResponse(errorStatements);
      return;
    }
    if (!statements) return;
    if (!statements.length) setStatementsDialogResponse('No Statements Found');
  }, [loadingStatements, errorStatements, statements]);

  const onSubmit = useCallback(async (searchParams: StatementSearchParams) => {
    if (searchParams.DateFrom === undefined || searchParams.DateTo === undefined) {
      setStatementsDialogResponse('Select the range');
      return;
    }
    if (searchParams.DateTo) {
      const date = new Date(searchParams.DateTo);
      const newDate = new Date(date.getFullYear(), date.getMonth() + 1, 1);
      searchParams.DateTo = newDate.getFullYear() + '-' + format(newDate, 'MM') + '-01';
    }

    getStatements(searchParams);
  }, []);

  useEffect(() => {
    if (debouncedFilterCriteria.DateFrom === undefined || debouncedFilterCriteria.DateTo === undefined) return;
    onSubmit({ ...debouncedFilterCriteria });
  }, [debouncedFilterCriteria, onSubmit]);

  const openPdf = async (fullname: string, currLimit: string, newLimit: number, isCredit: boolean) =>
    getCreditPdf({ data: { fullname, currLimit, newLimit, isCredit } }, true);

  const downloadPdf = async (fullname: string, currLimit: string, newLimit: number, name: string, isCredit: boolean) => {
    const filename = (name || 'CreditLimitForm').replace(/[\\:]/gim, '.') + '.pdf';
    getCreditPdf({ data: { fullname, currLimit, newLimit, isCredit }, filename });
  };

  const downloadUploadedPdf = async (requestId: string, name: string) => {
    const filename = (name || 'CreditLimitForm').replace(/[\\:]/gim, '.') + '.pdf';
    getUploadedCreditPdf({ requestId, filename });
  };

  useEffect(() => {
    if (loadingUploadCreditForm) return;
    if (errorUploadCreditForm) {
      setUploadingPdfRequestId('');
      return;
    }
    if (!successUploadedCreditForm) return;
    setUploadingPdfRequestId('');
    snackbar.success('Form has been uploaded');
    clearUploadedCreditForm();
  }, [loadingUploadCreditForm, errorUploadCreditForm, successUploadedCreditForm]);

  const onFileUpload = async (e: FileList | null) => {
    const requestId = currentUploadPdfRequestId;
    setCurrentUploadPdfRequestId('');
    if (!e) return;

    const uploadedFileArray = [];

    uploadedFileArray.push(e[0]);
    const uploadInput = document.getElementById('fileInput') as HTMLInputElement;
    if (uploadInput) uploadInput.value = '';

    setUploadingPdfRequestId(requestId);
    uploadCreditForm({ requestId, data: uploadedFileArray });
  };

  const downloadStatementHandler = async (statement: StatementEntity) => downloadStatement({ statementId: statement.statementId });

  const viewStatement = async (statement: StatementEntity) => downloadStatement({ statementId: statement.statementId }, true);

  useEffect(() => {
    if (loadingSetPartnerAccountNumber) return;
    if (errorSetPartnerAccountNumber) {
      setUpdatePartnerAccountNumberResult(errorSetPartnerAccountNumber);
    }
    if (!successSetPartnerAccountNumber) return;
    setUpdatePartnerAccountNumberResult('Partner Account Number has been successfully updated');
    clearSetPartnerAccountNumber();
  }, [loadingSetPartnerAccountNumber, errorSetPartnerAccountNumber, successSetPartnerAccountNumber]);

  const updatePartnerAccountNumber = async () => {
    if (!validateBankAccountNumber()) return;
    setPartnerAccountNumber({ accountId: account!.accId!.toString(), value: updatePartnerAccountNumberValue });
  };

  const availableBalance = useMemo(() => {
    if (accountDetails) return AmountUtils.formatAmount(accountDetails.available, '');
    else return undefined;
  }, [accountDetails]);

  return (
    <>
      <Breadcrumbs aria-label='breadcrumb' sx={{ marginBottom: '12px' }}>
        <Link sx={{ textTransform: 'uppercase', fontSize: '12px' }} underline='hover' color='inherit' href={RoutePaths.customers.onNavigate()}>
          Customers
        </Link>
        {customer && (
          <Link
            sx={{ textTransform: 'uppercase', fontSize: '12px' }}
            underline='hover'
            color='inherit'
            href={RoutePaths.customersDetails.onNavigate(selectedCustomerId!)}
          >
            Customers
          </Link>
        )}
        {customer && account && (
          <Link sx={{ textTransform: 'uppercase', fontSize: '12px' }} underline='none'>
            {account.accountNumber_M}
          </Link>
        )}
      </Breadcrumbs>
      <Grid container>
        <input id='fileInput' type='file' style={{ display: 'none' }} onChange={(e) => onFileUpload(e.currentTarget.files)} />
        <Grid item xs={12} md={4}>
          <DelayedLinearProgress loading={loadingCustomers || loadingAccounts} />
          <Card className={classes.card}>
            <CardHeader title='Account Details' />
            <CardContent>
              <Typography component='span' variant='body1'>
                <Grid container>
                  <Grid item xs={4}>
                    Account Status
                  </Grid>
                  <Grid item xs={8}>
                    {!account ? 'Loading account details' : account.status}
                  </Grid>
                  <Grid item xs={4}>
                    Available Balance
                  </Grid>
                  <Grid item xs={8}>
                    {!accountDetails ? 'Loading account details' : availableBalance}
                  </Grid>
                  <Grid item xs={4}>
                    Available Balance
                  </Grid>
                  <Grid item xs={8}>
                    {!accountDetails ? 'Loading account details' : availableBalance}
                  </Grid>
                  <Grid item xs={4}>
                    Customer
                  </Grid>
                  <Grid item xs={8}>
                    {!customer ? 'Loading account details' : customer.fullname}
                  </Grid>
                  <Grid item xs={4}>
                    Passport/ID
                  </Grid>
                  <Grid item xs={8}>
                    {!customer ? 'Loading account details' : customer.passport || ''}
                  </Grid>
                  <Grid item xs={4}>
                    Account Number
                  </Grid>
                  <Grid item xs={8}>
                    {!account ? 'Loading account details' : account.accountNumber_M}
                  </Grid>
                  <Grid item xs={4}>
                    Product
                  </Grid>
                  <Grid item xs={8}>
                    {!account ? 'Loading account details' : account.productName}
                  </Grid>
                  <Grid item xs={12} md={8}>
                    {mainApplicationId && (
                      <Button
                        variant='contained'
                        color='primary'
                        className={classes.accDetailsBtn}
                        onClick={() =>
                          navigate(RoutePaths.applicationsSupplementaryNew.onNavigate(mainApplicationId), { state: { accountId: selectedAccountId } })
                        }
                      >
                        Add Supplementary Application
                      </Button>
                    )}
                  </Grid>
                  {PermissionsUtils.hasPermission(PermissionType.TRANSACTIONS, PermissionLevel.READ) && (
                    <Grid item xs={12} md={8}>
                      <Button
                        variant='contained'
                        color='primary'
                        style={{ marginRight: '12px' }}
                        onClick={() => navigate(RoutePaths.customersTransactions.onNavigate(selectedCustomerId!, selectedAccountId!))}
                      >
                        Transactions
                      </Button>
                    </Grid>
                  )}
                  <Grid item xs={12} md={8}>
                    <Button variant='contained' color='primary' className={classes.accDetailsBtn} onClick={() => setStatementsDialogOpen(true)}>
                      Statements
                    </Button>
                  </Grid>
                  {PermissionsUtils.hasRole(RoleType.ADMIN) &&
                    PermissionsUtils.hasPermission(PermissionType.ACCOUNTS, PermissionLevel.EDIT) &&
                    account && (
                      <Grid item xs={12} md={12}>
                        <Button
                          variant='contained'
                          color='primary'
                          className={classes.accDetailsBtn}
                          onClick={() => {
                            setBankAccountNumberError('');
                            setUpdatePartnerAccountNumberValue(account?.partnerAccountNumber || '');
                            setShowUpdatePartnerAccountNumber(true);
                          }}
                        >
                          Update Partner Account Number
                        </Button>
                      </Grid>
                    )}
                </Grid>
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={8}>
          <DelayedLinearProgress loading={loadingCards} />
          <Card className={classes.card}>
            <CardHeader title='Cards' />
            {!cards && !loadingCards && (
              <CardContent>
                <Typography align='center'>
                  No data to display.
                  <br />
                  Start by performing a search.
                </Typography>
              </CardContent>
            )}
            {cards && customer && account && (
              <CardsList data={cards} customerData={customer} accountData={account} onRefresh={() => getCardsByAccount(selectedAccountId!)} />
            )}
          </Card>
          {PermissionsUtils.hasPermission(PermissionType.CREDIT_LIMIT_REQUESTS, PermissionLevel.READ) && (
            <>
              <DelayedLinearProgress loading={loadingCreditLimit} />
              <Card className={classes.card}>
                <CardHeader title='Manage Limit' />
                {!cards && !loadingCards && (
                  <CardContent>
                    <Typography align='center'>
                      No data to display.
                      <br />
                      Start by performing a search.
                    </Typography>
                  </CardContent>
                )}
                {accountCurrency && cards && cards.length && (
                  <CardContent>
                    <div className={classes.creditLimitRow}>
                      <Input
                        label='Opened Credit Limit'
                        value={creditLimit?.currentCreditLimit ?? ''}
                        disabled
                        InputProps={{ startAdornment: <InputAdornment position='start'>{accountCurrency}</InputAdornment> }}
                      />
                      <Input
                        id='requested-opened-limit'
                        type='number'
                        label='New Limit'
                        value={openedCreditLimit}
                        InputProps={{ startAdornment: <InputAdornment position='start'>{accountCurrency}</InputAdornment> }}
                        onChange={(e) => {
                          setOpenedCreditLimit(parseFloat(e.currentTarget.value));
                          if (parseFloat(e.currentTarget.value) > parseFloat(creditLimit?.currentCreditLimit || '0')) {
                            setHighestCreditRequested(parseFloat(e.currentTarget.value));
                          }
                        }}
                      />
                      <div></div>
                    </div>
                    <div className={classes.creditLimitRow}>
                      <Input
                        label='Pre-Approved Credit Limit'
                        value={creditLimit?.globalCreditLimit ?? ''}
                        disabled
                        InputProps={{ startAdornment: <InputAdornment position='start'>{accountCurrency}</InputAdornment> }}
                      />
                      <Input
                        type='number'
                        label='New Limit'
                        value={preapprovedCreditLimit ?? ''}
                        InputProps={{ startAdornment: <InputAdornment position='start'>{accountCurrency} </InputAdornment> }}
                        onChange={(e) => {
                          setPreapprovedCreditLimit(parseFloat(e.currentTarget.value));
                          if (parseFloat(e.currentTarget.value) > parseFloat(creditLimit?.globalCreditLimit || '0')) {
                            setHighestCreditRequested(parseFloat(e.currentTarget.value));
                          }
                        }}
                      />
                      {PermissionsUtils.hasPermission(PermissionType.CREDIT_LIMIT_REQUESTS, PermissionLevel.WRITE) ? (
                        <Button
                          variant='contained'
                          color='primary'
                          onClick={() => (openedCreditLimit ? setCreditLimitDialogOpen(true) : snackbar.error('Please input a credit limit'))}
                        >
                          Request New Limit
                        </Button>
                      ) : (
                        <div></div>
                      )}
                    </div>
                    <div className={classes.creditLimitRow}>
                      <Typography style={{ display: 'hidden' }} />
                      <Button
                        variant='contained'
                        color='primary'
                        onClick={() => {
                          setRequestsDialogOpen(true);
                          getRequests();
                        }}
                      >
                        View All Requests
                      </Button>
                    </div>
                  </CardContent>
                )}
              </Card>
            </>
          )}
        </Grid>
        <Dialog disableEscapeKeyDown maxWidth='md' aria-labelledby='confirmation-dialog-title' open={creditLimitDialogOpen}>
          <DialogTitle id='confirmation-dialog-title' style={{ textAlign: 'center' }}>
            Request New Limit
          </DialogTitle>
          <DialogContent dividers>
            <Grid container style={{ width: '400px' }}>
              <Grid item xs={6}>
                <Input
                  label='Opened Credit Limit'
                  value={creditLimit?.currentCreditLimit ?? ''}
                  disabled
                  InputProps={{ startAdornment: <InputAdornment position='start'>&euro;</InputAdornment> }}
                />
                <Input
                  label='Pre-Approved Credit Limit'
                  value={creditLimit?.globalCreditLimit ?? ''}
                  disabled
                  InputProps={{ startAdornment: <InputAdornment position='start'>&euro;</InputAdornment> }}
                />
              </Grid>
              <Grid item xs={6}>
                <Input
                  label='New Limit'
                  id='requested-opened-limit'
                  value={openedCreditLimit ?? ''}
                  onChange={(e) => {
                    setOpenedCreditLimit(parseFloat(e.currentTarget.value));
                    if (parseFloat(e.currentTarget.value) > preapprovedCreditLimit) {
                      setHighestCreditRequested(parseFloat(e.currentTarget.value));
                    }
                  }}
                  InputProps={{ startAdornment: <InputAdornment position='start'>&euro;</InputAdornment> }}
                />
                <Input
                  label='New Limit'
                  value={preapprovedCreditLimit ?? ''}
                  onChange={(e) => {
                    setPreapprovedCreditLimit(parseFloat(e.currentTarget.value));
                    if (parseFloat(e.currentTarget.value) > openedCreditLimit) {
                      setHighestCreditRequested(parseFloat(e.currentTarget.value));
                    }
                  }}
                  InputProps={{ startAdornment: <InputAdornment position='start'>&euro;</InputAdornment> }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogContent dividers style={{ textAlign: 'center' }}>
            <Typography>{!dialogResponse ? 'Loading' : dialogResponse}</Typography>
          </DialogContent>
          <DialogActions>
            {showConfirm && (
              <Button
                autoFocus
                loading={loadingChangeCreditLimit}
                onClick={() => changeCreditLimitHandler(cards ? cards[0].cardId : '0', openedCreditLimit, preapprovedCreditLimit)}
              >
                Confirm
              </Button>
            )}
            {!showConfirm && customer && creditLimit?.currentCreditLimit && highestCreditRequested && changedCreditLimit && (
              <Button
                autoFocus
                loading={loadingCreditPdf}
                onClick={() => {
                  openedCreditLimit > preapprovedCreditLimit
                    ? setHighestCreditRequested(openedCreditLimit)
                    : setHighestCreditRequested(preapprovedCreditLimit);
                  openPdf(customer.fullname, creditLimit.currentCreditLimit, highestCreditRequested, true);
                }}
              >
                Download Credit Form
              </Button>
            )}
            <Button
              autoFocus
              onClick={() => {
                setCreditLimitDialogOpen(false);
                setShowConfirm(true);
                setDialogResponse(defaultDialogResponse);
                setHighestCreditRequested(undefined);
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog disableEscapeKeyDown maxWidth='md' aria-labelledby='confirmation-dialog-title' open={requestsDialogOpen}>
          <DialogTitle id='confirmation-dialog-title' style={{ textAlign: 'center' }}>
            Requests
          </DialogTitle>
          <DialogContent dividers style={{ textAlign: 'center' }}>
            <DelayedLinearProgress loading={loadingRequests} />
            {!requests && !loadingRequests && <Typography>{requestsDialogResponse}</Typography>}
            {requests && (
              <Table aria-label='simple table' stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell style={{ width: '300px' }} align='left'>
                      Reference Number
                    </TableCell>
                    <TableCell style={{ width: '200px' }} align='center'>
                      Requested Credit Line
                    </TableCell>
                    <TableCell style={{ width: '200px' }} align='center'>
                      Request Status
                    </TableCell>
                    <TableCell style={{ width: '200px' }} align='center'>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {requests.map((row, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell align='center'>{row.name}</TableCell>
                      <TableCell align='center'>{row.proposedLimit} </TableCell>
                      <TableCell align='center'>{row.status}</TableCell>
                      <TableCell align='center'>
                        {row.status === 'Partner Approval' &&
                          PermissionsUtils.hasPermission(PermissionType.CREDIT_LIMIT_REQUESTS, PermissionLevel.APPROVE) &&
                          !row.isHistoryRecord && (
                            <>
                              <Tooltip title='Approve'>
                                <IconButton loading={approvingRequestId === row.id} onClick={() => approveRequestHandler(row)} size='large'>
                                  <DoneIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title='Reject'>
                                <IconButton loading={rejectingRequestId === row.id} onClick={() => rejectRequestHandler(row)} size='large'>
                                  <BlockIcon />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        {!row.isHistoryRecord && (
                          <>
                            <Tooltip title='Generate Form'>
                              <IconButton loading={downloadingPdfRequestId === row.id} onClick={() => downloadCreditForm(row)} size='large'>
                                <GetAppIcon />
                              </IconButton>
                            </Tooltip>
                            {PermissionsUtils.hasPermission(PermissionType.CREDIT_LIMIT_REQUESTS, PermissionLevel.WRITE) &&
                              PermissionsUtils.hasPermission(PermissionType.IPPFILES, PermissionLevel.WRITE) && (
                                <Tooltip title='Upload Form'>
                                  <IconButton loading={uploadingPdfRequestId === row.id} onClick={() => uploadCreditFormHandler(row)} size='large'>
                                    <UploadIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                            {row.hasAttachment && (
                              <Tooltip title='Download Uploaded Form'>
                                <IconButton
                                  loading={downloadingPdfRequestId === row.id + 'uploaded'}
                                  onClick={() => downloadUploadedCreditForm(row)}
                                  size='large'
                                >
                                  <DownloadDocumentIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {!requests?.length && (
                    <TableRow>
                      <TableCell colSpan={6} align='center'>
                        No Records Found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </DialogContent>
          <DialogActions>
            <Button autoFocus onClick={() => setRequestsDialogOpen(false)}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog disableEscapeKeyDown maxWidth='md' aria-labelledby='confirmation-dialog-title' open={transactionsDialogOpen}>
          <DialogTitle id='confirmation-dialog-title' style={{ textAlign: 'center' }}>
            Transactions
          </DialogTitle>
          <DialogContent dividers style={{ textAlign: 'center' }}>
            <DelayedLinearProgress loading={loadingTransactions} />
            {!transactions && !loadingTransactions && <Typography>{transactionsDialogResponse}</Typography>}
            {transactions && (
              <Table aria-label='simple table' stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell align='left'>Transaction Date</TableCell>
                    <TableCell align='center'>Transaction Details</TableCell>
                    <TableCell align='center'>Amount (Transaction Currency)</TableCell>
                    <TableCell align='center'>Amount (Account Currency)</TableCell>
                    <TableCell align='center'>FX Rate</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.map((row, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell align='center'>{DateUtils.formatDate(row.postingDate)}</TableCell>
                      <TableCell align='center'>{row.transactionDescription}</TableCell>
                      <TableCell align='center'>
                        {row.originalCurrency} {row.originalAmount}
                      </TableCell>
                      <TableCell align='center'>
                        {row.accountCurrency} {row.amount}
                      </TableCell>
                      <TableCell align='center'>{row.exchangeRate} </TableCell>
                    </TableRow>
                  ))}
                  {!transactions?.length && (
                    <TableRow>
                      <TableCell colSpan={5} align='center'>
                        No Records Found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </DialogContent>
          <DialogActions>
            <Button autoFocus onClick={() => setTransactionsDialogOpen(false)}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog disableEscapeKeyDown fullWidth maxWidth='md' aria-labelledby='confirmation-dialog-title' open={statementsDialogOpen}>
          <DialogTitle id='confirmation-dialog-title' style={{ textAlign: 'center' }}>
            Statements
          </DialogTitle>
          <DialogContent style={{ textAlign: 'center' }}>
            <Grid container>
              <Grid item xs={3}>
                <CustomDatePicker
                  name='DateFrom'
                  label='From'
                  views={['month', 'year']}
                  inputFormat='MM/yyyy'
                  fullWidth
                  validate
                  value={filterCriteria.DateFrom ? new Date(filterCriteria.DateFrom) : null}
                  onChange={(value) => onFilterChange('DateFrom', value)}
                />
              </Grid>
              <Grid item xs={3}>
                <CustomDatePicker
                  name='DateTo'
                  label='To'
                  views={['month', 'year']}
                  inputFormat='MM/yyyy'
                  fullWidth
                  validate
                  value={filterCriteria.DateTo ? new Date(filterCriteria.DateTo) : null}
                  onChange={(value) => onFilterChange('DateTo', value)}
                />
              </Grid>
              {/*<Grid item xs={3}>
              <DatePicker
                views={['month', 'year']}
                name="DateTo"
                format="MM/yyyy"
                label="To"
                margin="normal"
                fullWidth
                value={filterCriteria.DateTo ?? ''}
                onChange={(e: any) => {
                  onFilterChange('DateTo', e);
                }}
                KeyboardButtonProps={{
                  'aria-label': 'change date',
                }}
              />
            </Grid>*/}
            </Grid>
          </DialogContent>
          <DialogContent dividers style={{ textAlign: 'center' }}>
            <DelayedLinearProgress loading={loadingStatements} />
            {!statements && !loadingStatements && <Typography>{statementsDialogResponse}</Typography>}
            {statementRows && !loadingStatements && (
              <Table aria-label='simple table' stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell style={{ width: '200px' }} align='left'>
                      Account
                    </TableCell>
                    <TableCell style={{ width: '200px' }} align='left'>
                      Type
                    </TableCell>
                    <TableCell style={{ width: '200px' }} align='center'>
                      Statement Date
                    </TableCell>
                    <TableCell style={{ width: '200px' }} align='center'>
                      Due Date
                    </TableCell>
                    <TableCell style={{ width: '200px' }} align='center'>
                      Download
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {statementRows.map((row, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell align='left'>{row.accountNumberMasked}</TableCell>
                      <TableCell align='left'>{row.statementType}</TableCell>
                      <TableCell align='center'>{row.statementDate} </TableCell>
                      <TableCell align='center'>{row.dueDate} </TableCell>
                      <TableCell align='center'>
                        <IconButton size='large' loading={loadingDownloadStatement} onClick={() => viewStatement(row as StatementEntity)}>
                          <PrintIcon />
                        </IconButton>
                        <IconButton size='large' loading={loadingDownloadStatement} onClick={() => downloadStatementHandler(row as StatementEntity)}>
                          <GetAppIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              autoFocus
              onClick={() => {
                setStatementsDialogOpen(false);
                setFilterCriteria({ ...filterCriteria, DateFrom: undefined, DateTo: undefined });
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog disableEscapeKeyDown maxWidth='md' aria-labelledby='confirmation-dialog-title' open={showUpdatePartnerAccountNumber}>
          <DialogTitle id='confirmation-dialog-title' style={{ textAlign: 'center' }}>
            Update Account Partner Number
          </DialogTitle>
          <DialogContent dividers>
            <BankAccountInput
              name='bankAccountNumber'
              inputProps={{ className: classes.uppercase }}
              required
              label='Partner Account Number'
              error={!!bankAccountNumberError}
              helperText={bankAccountNumberError}
              fullWidth
              value={updatePartnerAccountNumberValue}
              onBlur={validateBankAccountNumber}
              onChange={(e) => {
                setUpdatePartnerAccountNumberValue(e.target.value);
                //onChangeAdjustment(e, 'bankAccountNumber');
                //setBankAccountNumberError(undefined);
              }}
            />
            {updatePartnerAccountNumberResult && <p>{updatePartnerAccountNumberResult}</p>}
          </DialogContent>
          <DialogActions>
            <Button loading={loadingSetPartnerAccountNumber} onClick={updatePartnerAccountNumber}>
              Update
            </Button>
            <Button autoFocus onClick={() => setShowUpdatePartnerAccountNumber(false)}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
        {creditPdf?.data && <PDFView objectUrl={URL.createObjectURL(creditPdf.data)} onClose={clearCreditPdf} />}
        {statementFile?.data && <PDFView objectUrl={URL.createObjectURL(statementFile.data)} onClose={clearDownloadedStatement} />}
      </Grid>
    </>
  );
};
